import {
  IArchivingJobExperimentResultDTO,
  ArchivingJobExperimentStatusDto,
} from 'business/models/Models';

export type UnarchivePreviewView = 'loading' | 'empty' | 'table';

// Pending or Running result rows mean an unarchive job is active. Used to block the UI
// immediately after POST /api/Archiving/unarchive and on reload, before the SignalR hub
// delivers an InProgress status.
export const hasActiveUnarchivingJobInResults = (
  results: IArchivingJobExperimentResultDTO[],
): boolean =>
  Array.isArray(results) &&
  results.some(
    (row) =>
      row.status === ArchivingJobExperimentStatusDto.Pending ||
      row.status === ArchivingJobExperimentStatusDto.Running,
  );

// Maps an unarchive experiment result status to its display label.
export const getUnarchiveStatusLabel = (
  status: ArchivingJobExperimentStatusDto | undefined,
): string => {
  switch (status) {
    case ArchivingJobExperimentStatusDto.Completed:
      return 'Restored';
    case ArchivingJobExperimentStatusDto.Failed:
      return 'Failed';
    default:
      return 'Scheduled';
  }
};

export type UnarchiveStatusVariant = 'error' | 'warning' | 'plain';

export type UnarchiveStatusView = {
  label: string;
  variant: UnarchiveStatusVariant;
  tooltip?: string;
};

// Derives how the Status column should render: a Failed row gets the error variant,
// a Restored row carrying a degradation errorMessage gets the warning variant, everything
// else is a plain label.
export const getUnarchiveStatusView = (
  status: ArchivingJobExperimentStatusDto | undefined,
  errorMessage?: string,
): UnarchiveStatusView => {
  const label = getUnarchiveStatusLabel(status);

  if (status === ArchivingJobExperimentStatusDto.Failed) {
    return { label, variant: 'error', tooltip: errorMessage };
  }

  if (status === ArchivingJobExperimentStatusDto.Completed && errorMessage) {
    return { label, variant: 'warning', tooltip: errorMessage };
  }

  return { label, variant: 'plain' };
};

/**
 * View-state for the unarchive Preview table. Mirrors the archiving
 * `getPreviewTableView` pattern but kept local so the feature does not depend
 * on archiving's heavy `utils` module.
 */
export const getUnarchivePreviewView = ({
  isLoading,
  hasSelection,
  rowCount,
  isJobActive = false,
}: {
  isLoading: boolean;
  hasSelection: boolean;
  rowCount: number;
  isJobActive?: boolean;
}): UnarchivePreviewView => {
  if (isLoading) {
    return 'loading';
  }
  if (isJobActive || !hasSelection || rowCount === 0) {
    return 'empty';
  }
  return 'table';
};

export const getExperimentRoutePath = (experimentId: number | string): string =>
  `/exp/${experimentId}`;
